import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import qs from 'qs';
import { createPath } from 'history';
import type { Location } from 'history';
import _ from 'lodash';
import { isPath } from '../../../routes/config';
import { SEARCH } from '../../../routes/config/pageTypes';
import { queryToStateQuery, stateToQuery } from '../../../features/request/reducers/queryReducer';
import type { RequestState, RequestStateQuery } from '../../../features/request/types';
import initialState from '../../../features/request/config/initialState';

const params = [
  'page',
  'country_code',
  'region_1',
  'region_2',
  'arrival',
  'duration_of_stay',
  'max_occupancy',
  'bedrooms',
  'bathrooms',
  'amenities',
  'distance_to_beach',
  'accommodation_location',
  'activities',
  'accommodation_type',
  'stars',
  'min_price',
  'max_price',
];

function normalizeQuery(query: RequestStateQuery): RequestStateQuery {
  const stateFromQuery = queryToStateQuery(query);
  const state: RequestState = {
    ...initialState,
    ...stateFromQuery.requestState,
    filterSet: {
      ...initialState.filterSet,
      ...stateFromQuery.filterSet,
    },
    options: {
      ...initialState.options,
      ...stateFromQuery.filterOptions,
    },
  };
  return stateToQuery(state);
}

function isQueryDifferent(
  baseQuery: RequestStateQuery | undefined,
  otherQuery: RequestStateQuery,
): boolean {
  if (baseQuery === undefined && otherQuery === undefined) {
    return false;
  }
  if (baseQuery === undefined || otherQuery === undefined) {
    return true;
  }
  return params.some((param) => !_.isEqual((baseQuery as any)[param], (otherQuery as any)[param]));
}

function queryFromLocation(location: Location): RequestStateQuery {
  return qs.parse(location.search, { ignoreQueryPrefix: true });
}

interface SearchUrlProps {
  query: RequestStateQuery;
  // Called to update the global state of the search
  onChange: (locationQuery: RequestStateQuery | undefined) => void;
  // Called to trigger a new search request
  onUrlUpdate: (query: string) => void;
}

export default function SearchUrl({ query, onChange, onUrlUpdate }: SearchUrlProps): null {
  const location = useLocation();
  const navigate = useNavigate();
  const initialRender = useRef(true);

  function navigateToNewUrl(queryToNavigate: RequestStateQuery): void {
    const { pathname, hash } = location;
    const nextLocation = createPath({
      pathname,
      hash,
      search: `?${qs.stringify(queryToNavigate, { arrayFormat: 'brackets' })}`,
    });

    navigate(nextLocation);
  }

  useEffect(() => {
    if (!isPath(SEARCH, location.pathname)) {
      return;
    }

    const locationQuery = queryFromLocation(location);
    const isDifferentQuery = isQueryDifferent(normalizeQuery(locationQuery), query);
    if (!isDifferentQuery) {
      initialRender.current = false;
      return;
    }

    if (initialRender.current) {
      initialRender.current = false;
      onChange(locationQuery);
      onUrlUpdate(`?${qs.stringify(locationQuery, { arrayFormat: 'brackets' })}`);
    } else if (isDifferentQuery) {
      onUrlUpdate(`?${qs.stringify(query, { arrayFormat: 'brackets' })}`);
      navigateToNewUrl(query);
    }
  }, [location, query]);

  return null;
}
